// @workflow tasks.show-in-3d
/**
 * One permission map (SYNTHESIS.md §4, piece 4): each app capability → the permission keys that
 * gate it today, read by the buttons (`v-if` / `disabled`) and by the UI tool runner, so a button
 * and the assistant always agree. Client UX only: the server stays the authority.
 * Capabilities are added one at a time, as each is moved behind its single action.
 * No alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import permissions from '../features/Share/config/permissions.js';
import { userHasPermissions } from '../utils/permissionRule.js';

export const CAPABILITY = Object.freeze({
  AI_ANALYSIS_VIEW: 'ai-analysis.view',
  AI_ANALYSIS_RUN: 'ai-analysis.run',
  AI_ANALYSIS_REVIEW: 'ai-analysis.review',
  /** Capability 20: show tasks' tags in the 3D (or 2D / 360) viewer. */
  TASKS_SHOW_IN_3D: 'tasks.show-in-3d',
  /** Open a task on its task board, with its Edit task open (actions/openTaskOnBoard.js). */
  TASKS_OPEN_BOARD: 'tasks.open-board',
  /** Open the Register Import screen for a register (Register Management, "Import from Excel"). */
  REGISTERS_IMPORT: 'registers.import',
  /** Show a tag on its level's 2D plan (actions/tagIn2d.js): the tag card's 2D button and `ui.tags.show_in_2d`. */
  TAGS_SHOW_IN_2D: 'tags.show-in-2d',
});

/**
 * Opening a task board at all (task.routes.js: Kanban, List and Charts need Task Tracker view,
 * Gantt needs Gantt view, and Kanban sends a user without its permission to Gantt): either key.
 */
const TASK_BOARD_RULE = Object.freeze({
  keys: Object.freeze([permissions.TaskTracker.View, permissions.Gantt.View]),
  any: true,
  label: 'the task board',
});

/**
 * `keys` with `any: true`: one of them is enough. `label` finishes "You don't have access to …".
 * Show tasks in 3D: no button checks a permission of its own today; what gates it is opening a
 * task board, as for opening a task on its board.
 */
const RULES = Object.freeze({
  [CAPABILITY.AI_ANALYSIS_VIEW]: Object.freeze({ keys: [permissions.Images360.View, permissions.AIAnalysis.View], label: 'AI analysis results' }),
  [CAPABILITY.AI_ANALYSIS_RUN]: Object.freeze({ keys: [permissions.Images360.View, permissions.AIAnalysis.View, permissions.AIAnalysis.Run], label: 'running AI analysis' }),
  [CAPABILITY.AI_ANALYSIS_REVIEW]: Object.freeze({ keys: [permissions.Images360.View, permissions.AIAnalysis.View, permissions.AIAnalysis.Review], label: 'reviewing AI findings' }),
  [CAPABILITY.TASKS_SHOW_IN_3D]: TASK_BOARD_RULE,
  [CAPABILITY.TASKS_OPEN_BOARD]: TASK_BOARD_RULE,
  /** The Register Management page (route meta Registers.View) and its "Import from Excel" row action (Registers.Import): both. */
  [CAPABILITY.REGISTERS_IMPORT]: Object.freeze({
    keys: Object.freeze([permissions.Registers.View, permissions.Registers.Import]),
    any: false,
    label: 'register import',
  }),
  /** The 2D plot plans (the tag card's 2D button, components/search/nodeControls.vue). */
  [CAPABILITY.TAGS_SHOW_IN_2D]: Object.freeze({
    keys: Object.freeze([permissions.PlotPlans2D.Access]),
    any: false,
    label: 'the 2D plot plans',
  }),
});

const ruleOf = (capability) => (Object.prototype.hasOwnProperty.call(RULES, capability) ? RULES[capability] : null);

/** @returns {string[]} today's permission keys for `capability` (none for an unknown one) */
export function permissionKeysFor(capability) {
  const rule = ruleOf(capability);
  return rule ? [...rule.keys] : [];
}

/** @returns {string|null} what the user lacks, in their words, for "You don't have access to …" */
export function capabilityLabel(capability) {
  const rule = ruleOf(capability);
  return rule ? rule.label : null;
}

/**
 * May the signed-in user use `capability`? An unknown capability is never allowed.
 * @param {string} capability a CAPABILITY value
 * @param {{ getters?: object }|null} store the app's store (the user is `ACCOUNT/GET_USER`)
 * @returns {boolean}
 */
export function can(capability, store) {
  const rule = ruleOf(capability);
  if (!rule) return false;
  const user = store && store.getters ? store.getters['ACCOUNT/GET_USER'] : null;
  return userHasPermissions(user, rule.keys, rule.any);
}
